import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { fetchProductionCostsApi } from "../../api/productionCost.api";

export interface ProductionCostPagination {
  page: number;
  limit: number;
  totalItems: number;
  totalPages: number;
}

export interface ProductionCostItem {
  _id: string;
  serialNumber: string;
  loggerSerialNumber: string;
  manufacturingSerialNumber: string;
  productName: string;
  totalCost: number;
  warehouseName: string;
  createdAt: string;
  totalMaterialCost: number;
  totalTravelCost: number;
  totalOtherCost: number;
  totalTransferCost?: number;
  materialsUsed: Array<{
    rawMaterialId: string;
    rawMaterialName: string;
    quantityUsed: number;
    convertedUnit: string;
    materialCost: number;
    travelCost: number;
    otherCost: number;
    totalCost: number;
  }>;
}

export interface ProductionCostState {
  items: ProductionCostItem[];
  pagination: ProductionCostPagination;
  loading: boolean;
  error: string | null;
}

interface FetchArgs {
  page: number;
  limit: number;
  search: string;
}

const initialPagination: ProductionCostPagination = {
  page: 1,
  limit: 10,
  totalItems: 0,
  totalPages: 1,
};

const initialState: ProductionCostState = {
  items: [],
  pagination: initialPagination,
  loading: false,
  error: null,
};

export const fetchProductionCosts = createAsyncThunk<
  { items: ProductionCostItem[]; pagination: ProductionCostPagination },
  FetchArgs,
  { rejectValue: string }
>("productionCost/fetch", async (params, { rejectWithValue }) => {
  try {
    const res = await fetchProductionCostsApi(params);
    return res;
  } catch (err: any) {
    return rejectWithValue(
      err.response?.data?.message || err.message || "Network Error"
    );
  }
});

const productionCostSlice = createSlice({
  name: "productionCost",
  initialState,
  reducers: {
    clearCostError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchProductionCosts.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchProductionCosts.fulfilled, (state, action) => {
        state.loading = false;
        state.items = action.payload.items;
        state.pagination = action.payload.pagination;
      })
      .addCase(fetchProductionCosts.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Unknown error occurred.";
        state.items = [];
        state.pagination = initialPagination;
      });
  },
});

export const { clearCostError } = productionCostSlice.actions;
export default productionCostSlice.reducer;
